import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { useSnackbar } from '../context/SnackbarContext';
import './AdminMembershipPlans.css';

interface MembershipPlan {
  id: string;
  name: string;
  minimumDeposit: number;
  percentageDiscount: number;
  cashbackEnabled: boolean;
  cashbackPercentage: number;
  cashbackTiming: 'on_topup' | 'on_spend';
  priceMatching: boolean;
  pointsConversionRate: number;
  freeDelivery: boolean;
  status: 'active' | 'inactive' | 'closed' | 'teaser';
  subscriberCount?: number;
}

const emptyForm = {
  name: '',
  minimumDeposit: '',
  percentageDiscount: '',
  cashbackEnabled: false,
  cashbackPercentage: '',
  cashbackTiming: 'on_topup' as 'on_topup' | 'on_spend',
  pointsConversionRate: '',
  priceMatching: false,
  freeDelivery: false,
  status: 'active' as 'active' | 'inactive' | 'closed' | 'teaser',
};

const AdminMembershipPlans: React.FC = () => {
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState<string | null>(null);
  const { showSuccess, showError } = useSnackbar();
  const formRef = useRef<HTMLDivElement | null>(null);
  const isFormDirty = useMemo(() => {
    const base = editingId ? plans.find(plan => plan.id === editingId) : null;
    if (base) {
      return (
        form.name !== base.name ||
        form.minimumDeposit !== base.minimumDeposit.toString() ||
        form.percentageDiscount !== base.percentageDiscount.toString() ||
        form.cashbackEnabled !== base.cashbackEnabled ||
        form.cashbackPercentage !== base.cashbackPercentage.toString() ||
        form.cashbackTiming !== base.cashbackTiming ||
        form.pointsConversionRate !== base.pointsConversionRate.toString() ||
        form.priceMatching !== base.priceMatching ||
        form.freeDelivery !== base.freeDelivery ||
        form.status !== base.status
      );
    }
    return Object.keys(emptyForm).some(key => {
      const field = (form as Record<string, any>)[key];
      const baseField = (emptyForm as Record<string, any>)[key];
      return field !== baseField && field !== '';
    });
  }, [editingId, form, plans]);

  const confirmDiscard = () => {
    if (!isFormDirty) return true;
    return window.confirm('You have unsaved changes. Discard them and continue?');
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/admin/membership-plans');
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching membership plans:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) {
      showError('Plan name is required');
      return;
    }

    const payload = {
      name: form.name.trim(),
      minimumDeposit: parseFloat(form.minimumDeposit) || 0,
      percentageDiscount: parseFloat(form.percentageDiscount) || 0,
      cashbackEnabled: form.cashbackEnabled,
      cashbackPercentage: parseFloat(form.cashbackPercentage) || 0,
      cashbackTiming: form.cashbackTiming,
      pointsConversionRate: parseFloat(form.pointsConversionRate) || 0,
      priceMatching: form.priceMatching,
      freeDelivery: form.freeDelivery,
      status: form.status,
    };

    try {
      if (editingId) {
        await api.put(`/admin/membership-plans/${editingId}`, payload);
        showSuccess('Membership plan updated');
      } else {
        await api.post('/admin/membership-plans', payload);
        showSuccess('Membership plan created');
      }
      setForm({ ...emptyForm });
      setEditingId(null);
      fetchPlans();
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to save plan');
    }
  };

  const handleEdit = (plan: MembershipPlan) => {
    if (editingId !== plan.id && !confirmDiscard()) {
      return;
    }
    setEditingId(plan.id);
    setForm({
      name: plan.name,
      minimumDeposit: plan.minimumDeposit.toString(),
      percentageDiscount: plan.percentageDiscount.toString(),
      cashbackEnabled: plan.cashbackEnabled,
      cashbackPercentage: plan.cashbackPercentage.toString(),
      cashbackTiming: plan.cashbackTiming,
      pointsConversionRate: plan.pointsConversionRate.toString(),
      priceMatching: plan.priceMatching,
      freeDelivery: plan.freeDelivery,
      status: plan.status || 'active',
    });
    window.setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const handleDelete = async (planId: string) => {
    try {
      await api.delete(`/admin/membership-plans/${planId}`);
      showSuccess('Membership plan deleted');
      fetchPlans();
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to delete plan');
    }
  };

  return (
    <div className="container admin-membership-page">
      <div className="admin-membership-header">
        <h1>Membership Plans</h1>
        <p>Define plan rules for deposits, discounts, and benefits.</p>
      </div>

      <div className="membership-form-card" ref={formRef}>
        <h2>{editingId ? 'Edit Plan' : 'Create Plan'}</h2>
        {editingId && (() => {
          const plan = plans.find(p => p.id === editingId);
          const count = plan?.subscriberCount ?? 0;
          if (count <= 0) return null;
          return (
            <div className="plan-subscribers-warning" role="alert">
              This plan has <strong>{count}</strong> {count === 1 ? 'subscriber' : 'subscribers'}. Changes will affect their benefits.
            </div>
          );
        })()}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Plan Name</label>
              <input
                value={form.name}
                onChange={event => setForm({ ...form, name: event.target.value })}
                placeholder="Premium Plus"
                required
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                value={form.status}
                onChange={event =>
                  setForm({ ...form, status: event.target.value as 'active' | 'inactive' | 'closed' | 'teaser' })
                }
              >
                <option value="active">Active</option>
                <option value="teaser">Teaser (Coming soon)</option>
                <option value="inactive">Inactive</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="form-group">
              <label>Minimum Deposit</label>
              <input
                type="number"
                value={form.minimumDeposit}
                onChange={event => setForm({ ...form, minimumDeposit: event.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Percentage Discount</label>
              <input
                type="number"
                value={form.percentageDiscount}
                onChange={event => setForm({ ...form, percentageDiscount: event.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Cashback Percentage</label>
              <input
                type="number"
                value={form.cashbackPercentage}
                onChange={event => setForm({ ...form, cashbackPercentage: event.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Cashback Timing</label>
              <select
                value={form.cashbackTiming}
                onChange={event =>
                  setForm({ ...form, cashbackTiming: event.target.value as 'on_topup' | 'on_spend' })
                }
              >
                <option value="on_topup">On top-up</option>
                <option value="on_spend">On spend</option>
              </select>
            </div>
            <div className="form-group">
              <label>Points Conversion Rate</label>
              <input
                type="number"
                value={form.pointsConversionRate}
                onChange={event => setForm({ ...form, pointsConversionRate: event.target.value })}
              />
            </div>
          </div>

          <div className="checkbox-grid">
            <label>
              <input
                type="checkbox"
                checked={form.cashbackEnabled}
                onChange={event => setForm({ ...form, cashbackEnabled: event.target.checked })}
              />
              Cashback Enabled
            </label>
            <label>
              <input
                type="checkbox"
                checked={form.priceMatching}
                onChange={event => setForm({ ...form, priceMatching: event.target.checked })}
              />
              Price Matching
            </label>
            <label>
              <input
                type="checkbox"
                checked={form.freeDelivery}
                onChange={event => setForm({ ...form, freeDelivery: event.target.checked })}
              />
              Free Delivery
            </label>
          </div>
          {form.cashbackEnabled && parseFloat(form.cashbackPercentage || '0') > 0 && parseFloat(form.percentageDiscount || '0') > 0 && (
            <div className="plans-warning">
              Cashback and discount are both enabled. Make sure this is intentional.
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              {editingId ? 'Update Plan' : 'Add Plan'}
            </button>
            {editingId && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setEditingId(null);
                  setForm({ ...emptyForm });
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="membership-table-card">
        <h2>Existing Plans</h2>
        {loading ? (
          <div className="plan-skeleton-grid">
            {[0, 1, 2].map((item) => (
              <div key={item} className="plan-skeleton-card">
                <div className="plan-skeleton-line title" />
                <div className="plan-skeleton-line meta" />
                <div className="plan-skeleton-line meta short" />
              </div>
            ))}
          </div>
        ) : plans.length === 0 ? (
          <div className="no-plans">
            <div className="no-plans-icon">📋</div>
            <h3>No membership plans yet</h3>
            <p>Create a plan to unlock member pricing and perks.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                if (!confirmDiscard()) return;
                setEditingId(null);
                setForm({ ...emptyForm });
                window.setTimeout(() => {
                  formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 0);
              }}
            >
              Create plan
            </button>
          </div>
        ) : (
          <div className="plans-grid">
            {plans.map(plan => (
              <div key={plan.id} className="plan-card">
                <div className="plan-header">
                  <h3>{plan.name}</h3>
                  <div className="plan-actions">
                    <button className="btn btn-secondary" onClick={() => handleEdit(plan)}>
                      Edit
                    </button>
                    <button className="btn btn-danger" onClick={() => handleDelete(plan.id)}>
                      Delete
                    </button>
                  </div>
                </div>
                <div className="plan-detail">
                  <span>Status</span>
                  <strong>{plan.status}</strong>
                </div>
                <div className="plan-detail">
                  <span>Subscribers</span>
                  <strong>{plan.subscriberCount ?? 0}</strong>
                </div>
                <div className="plan-detail">
                  <span>Minimum Deposit</span>
                  <strong>${plan.minimumDeposit.toFixed(2)}</strong>
                </div>
                <div className="plan-detail">
                  <span>Percentage Discount</span>
                  <strong>{plan.percentageDiscount}%</strong>
                </div>
                <div className="plan-detail">
                  <span>Cashback</span>
                  <strong>
                    {plan.cashbackEnabled ? `${plan.cashbackPercentage}%` : 'Disabled'}
                  </strong>
                </div>
                <div className="plan-detail">
                  <span>Cashback Timing</span>
                  <strong>{plan.cashbackTiming === 'on_spend' ? 'On spend' : 'On top-up'}</strong>
                </div>
                <div className="plan-detail">
                  <span>Points Conversion Rate</span>
                  <strong>{plan.pointsConversionRate}</strong>
                </div>
                <div className="plan-flags">
                  <span>{plan.priceMatching ? 'Price Matching' : 'No Price Matching'}</span>
                  <span>{plan.freeDelivery ? 'Free Delivery' : 'Delivery Fees Apply'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMembershipPlans;
